// S1 "New product" flow — template picker FIRST (§4: "New product (template
// picker first)"), then a draft form. A modal overlay so it works on the list
// route without a new router entry. On success it hands the new product id back
// so the screen can deep-link into S2. Uses only foundation hooks + the shared
// schema-driven attribute form.
//
// UX-2: the modal is the area's `.cat-scrim` / `.cat-modal` shell and the form
// is the `.cat-form` field grid — the same shape every other create surface in
// the catalogue uses. `button.template-card`, the `#create-*` field ids and the
// `create-product-submit` handle are M01-S01/S02 contracts and are unchanged.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { Button } from "../../../../../components/button";
import { Banner } from "../../../../../components/toast";
import { PRODUCT_KINDS, type Product, type ProductKind } from "../../../../../lib/api";
import type { FieldError } from "../../../../../lib/request";
import { RequestError } from "../../../../../lib/request";
import { SchemaAttributeForm, validateAttributes } from "../../components/schema-form";
import {
  useAttributeSchemas,
  useCreateProduct,
  useTemplates,
  useUoms,
} from "../../hooks/catalogue-queries";

type AttributeSchema = components["schemas"]["AttributeSchema"];
type CatalogueTemplate = components["schemas"]["CatalogueTemplate"];

interface TemplateChoice {
  /** undefined = "blank" (no template_code). */
  template: CatalogueTemplate | undefined;
}

export function ProductCreatePanel({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (product: Product) => void;
}) {
  const { t } = useTranslation();
  const [choice, setChoice] = useState<TemplateChoice | undefined>(undefined);

  return (
    <div
      className="cat-scrim"
      role="dialog"
      aria-modal="true"
      aria-label={t("catalogue.productsList.create.pickTitle")}
    >
      <div className="cat-modal">
        {choice === undefined ? (
          <TemplateStep
            onPick={(template) => {
              setChoice({ template });
            }}
            onClose={onClose}
          />
        ) : (
          <FormStep
            template={choice.template}
            onBack={() => {
              setChoice(undefined);
            }}
            onClose={onClose}
            onCreated={onCreated}
          />
        )}
      </div>
    </div>
  );
}

function CloseButton({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className="ds-iconbtn cat-row-end"
      onClick={onClose}
      aria-label={t("catalogue.state.close")}
    >
      ✕
    </button>
  );
}

function TemplateStep({
  onPick,
  onClose,
}: {
  onPick: (template: CatalogueTemplate | undefined) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const templates = useTemplates();
  const productTemplates = (templates.data?.items ?? []).filter(
    (tpl) => tpl.kind === "product" && tpl.status === "active",
  );

  return (
    <>
      <header className="cat-modal-head">
        <h2>{t("catalogue.productsList.create.pickTitle")}</h2>
        <CloseButton onClose={onClose} />
      </header>

      <div className="cat-modal-body">
        <p className="cat-note">{t("catalogue.productsList.create.pickBody")}</p>

        <ul className="template-gallery">
          <li>
            <button
              type="button"
              className="template-card"
              onClick={() => {
                onPick(undefined);
              }}
            >
              <span className="template-card-name">{t("catalogue.productsList.create.blank")}</span>
              <span className="template-card-hint">
                {t("catalogue.productsList.create.blankHint")}
              </span>
            </button>
          </li>
          {productTemplates.map((tpl) => (
            <li key={tpl.id}>
              <button
                type="button"
                className="template-card"
                onClick={() => {
                  onPick(tpl);
                }}
              >
                <span className="template-card-name">{tpl.name}</span>
                <span className="template-card-hint">{tpl.code}</span>
              </button>
            </li>
          ))}
        </ul>

        {templates.isError ? (
          <Banner kind="bad">{t("catalogue.productsList.create.templatesError")}</Banner>
        ) : productTemplates.length === 0 && !templates.isPending ? (
          <p className="cat-hint">{t("catalogue.productsList.create.noTemplates")}</p>
        ) : null}
      </div>
    </>
  );
}

function FormStep({
  template,
  onBack,
  onClose,
  onCreated,
}: {
  template: CatalogueTemplate | undefined;
  onBack: () => void;
  onClose: () => void;
  onCreated: (product: Product) => void;
}) {
  const { t } = useTranslation();
  const uoms = useUoms();
  const schemas = useAttributeSchemas();
  const create = useCreateProduct();

  const [sku, setSku] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState<ProductKind>("finished_good");
  const [baseUomId, setBaseUomId] = useState("");
  const [schemaId, setSchemaId] = useState("");
  const [attributes, setAttributes] = useState<Record<string, unknown>>({});
  const [serverErrors, setServerErrors] = useState<readonly FieldError[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const activeSchemas = (schemas.data?.items ?? []).filter(
    (s): s is AttributeSchema => s.status === "active",
  );
  const selectedSchema = activeSchemas.find((s) => s.id === schemaId);
  const jsonSchema = selectedSchema?.json_schema;
  const uiHints = selectedSchema?.ui_hints;

  const attrErrors = validateAttributes(jsonSchema, attributes);
  const canSubmit =
    sku.trim().length > 0 &&
    name.trim().length > 0 &&
    baseUomId.length > 0 &&
    attrErrors.length === 0 &&
    !create.isPending;

  function submit() {
    setErrorMsg(null);
    setServerErrors([]);
    const body: components["schemas"]["ProductCreateRequest"] = {
      sku: sku.trim(),
      name: name.trim(),
      kind,
      base_uom_id: baseUomId,
    };
    if (schemaId.length > 0) body.attribute_schema_id = schemaId;
    if (schemaId.length > 0 && Object.keys(attributes).length > 0) body.attributes = attributes;
    if (template !== undefined) body.template_code = template.code;
    create.mutate(body, {
      onSuccess: (product) => {
        onCreated(product);
      },
      onError: (err: unknown) => {
        if (err instanceof RequestError && err.fieldErrors.length > 0) {
          setServerErrors(err.fieldErrors);
        }
        setErrorMsg(err instanceof Error ? err.message : t("catalogue.productsList.create.error"));
      },
    });
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <header className="cat-modal-head">
        <h2>{t("catalogue.productsList.create.formTitle")}</h2>
        <CloseButton onClose={onClose} />
      </header>

      <div className="cat-modal-body">
        {template !== undefined ? (
          <p className="cat-note">
            {t("catalogue.productsList.create.fromTemplate", { name: template.name })}
          </p>
        ) : null}

        <div className="cat-form">
          <div className="cat-fld">
            <label htmlFor="create-sku">{t("catalogue.productsList.create.sku")}</label>
            <input
              id="create-sku"
              value={sku}
              onChange={(e) => {
                setSku(e.target.value);
              }}
              required
            />
          </div>

          <div className="cat-fld">
            <label htmlFor="create-name">{t("catalogue.productsList.create.name")}</label>
            <input
              id="create-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              required
            />
          </div>

          <div className="cat-fld">
            <label htmlFor="create-kind">{t("catalogue.productsList.create.kind")}</label>
            <select
              id="create-kind"
              value={kind}
              onChange={(e) => {
                setKind(e.target.value as ProductKind);
              }}
            >
              {PRODUCT_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`kind.${k}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="cat-fld">
            <label htmlFor="create-uom">{t("catalogue.productsList.create.baseUom")}</label>
            <select
              id="create-uom"
              value={baseUomId}
              onChange={(e) => {
                setBaseUomId(e.target.value);
              }}
              required
            >
              <option value="" disabled>
                {uoms.isPending
                  ? t("catalogue.productDetail.uom.loading")
                  : t("catalogue.productsList.create.baseUomPlaceholder")}
              </option>
              {(uoms.data?.items ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.code} — {u.name}
                </option>
              ))}
            </select>
          </div>

          <div className="cat-fld">
            <label htmlFor="create-schema">{t("catalogue.productsList.create.schema")}</label>
            <select
              id="create-schema"
              value={schemaId}
              onChange={(e) => {
                setSchemaId(e.target.value);
                setAttributes({});
              }}
            >
              <option value="">
                {schemas.isPending
                  ? t("catalogue.productsList.create.schemaLoading")
                  : t("catalogue.productsList.create.schemaPlaceholder")}
              </option>
              {activeSchemas.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {selectedSchema !== undefined ? (
            <fieldset className="create-attributes cat-form-wide">
              <legend>{t("catalogue.productsList.create.attributesTitle")}</legend>
              <SchemaAttributeForm
                jsonSchema={jsonSchema}
                uiHints={uiHints}
                value={attributes}
                onChange={setAttributes}
                serverErrors={serverErrors}
                disabled={create.isPending}
              />
            </fieldset>
          ) : null}
        </div>

        {errorMsg !== null ? <Banner kind="bad">{errorMsg}</Banner> : null}
      </div>

      <div className="cat-modal-foot">
        <Button onClick={onBack}>{t("catalogue.productsList.create.back")}</Button>
        <div className="cat-modal-foot-right">
          <Button onClick={onClose}>{t("catalogue.productsList.create.cancel")}</Button>
          <Button
            type="submit"
            variant="primary"
            // Locale-independent handle — see the note on `new-product`.
            data-testid="create-product-submit"
            disabled={!canSubmit}
            busy={create.isPending}
            busyLabel={t("catalogue.productsList.create.submitting")}
          >
            {t("catalogue.productsList.create.submit")}
          </Button>
        </div>
      </div>
    </form>
  );
}
